"use client";

import {
  Button,
  confirm,
  DataTable,
  Input,
  Label,
  Loader,
  Modal,
  Switch,
  type TanstackReactTable,
} from "@devhop/ui";
import { useParams } from "@tanstack/react-router";
import {
  EditIcon,
  PlusIcon,
  SearchIcon,
  Trash2Icon,
  WrenchIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { ShopServiceRow } from "@/modules/merchant/presentation/api/client";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { formatPrice } from "@/shared/lib/currency";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useCreateServiceMutation,
  useDeleteServiceMutation,
  useShopServicesQuery,
  useUpdateServiceMutation,
} from "../api/queries";

export function ShopServicesPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;

  const canCreate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.catalog.create,
    verifiedLevel,
  );
  const canUpdate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.catalog.update,
    verifiedLevel,
  );
  const canDelete = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.catalog.delete,
    verifiedLevel,
  );

  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);

  const listQ = useShopServicesQuery(shopId, {
    q: search.trim() || undefined,
    limit,
    offset,
  });

  const createMut = useCreateServiceMutation(shopId);
  const updateMut = useUpdateServiceMutation(shopId);
  const deleteMut = useDeleteServiceMutation(shopId);

  // create / edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ShopServiceRow | null>(null);
  const [nameInput, setNameInput] = useState("");
  const [priceInput, setPriceInput] = useState("");

  const openCreate = () => {
    setEditing(null);
    setNameInput("");
    setPriceInput("");
    setModalOpen(true);
  };
  const openEdit = (row: ShopServiceRow) => {
    setEditing(row);
    setNameInput(row.name);
    setPriceInput(String(row.price));
    setModalOpen(true);
  };

  const submit = () => {
    const name = nameInput.trim();
    const price = Number(priceInput);
    if (!name || !Number.isFinite(price) || price < 0) return;
    if (editing) {
      updateMut.mutate(
        { shopProductSkuId: editing.id, body: { name, price } },
        { onSuccess: () => setModalOpen(false) },
      );
    } else {
      createMut.mutate(
        { name, price },
        { onSuccess: () => setModalOpen(false) },
      );
    }
  };

  const columns: TanstackReactTable.ColumnDef<ShopServiceRow>[] = useMemo(
    () => [
      {
        id: "name",
        header: "ຊື່ບໍລິການ",
        size: 240,
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        id: "price",
        header: "ລາຄາ",
        size: 120,
        cell: ({ row }) =>
          formatPrice(row.original.price, row.original.currency),
      },
      ...(canUpdate
        ? [
            {
              id: "active",
              header: "ເປີດໃຊ້",
              size: 84,
              cell: ({ row }) => {
                const r = row.original;
                return (
                  <Switch
                    checked={r.isActive}
                    onCheckedChange={(v) =>
                      updateMut.mutate({
                        shopProductSkuId: r.id,
                        body: { isActive: v },
                      })
                    }
                    aria-label={r.isActive ? "ປິດການໃຊ້ງານ" : "ເປີດການໃຊ້ງານ"}
                  />
                );
              },
            } as TanstackReactTable.ColumnDef<ShopServiceRow>,
          ]
        : []),
      // ➡️ action ຢູ່ຂວາສຸດ
      ...(canUpdate || canDelete
        ? [
            {
              id: "actions",
              header: "ຈັດການ",
              size: 190,
              cell: ({ row }) => {
                const r = row.original;
                return (
                  <div className="flex justify-center gap-1.5">
                    {canUpdate ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={() => openEdit(r)}
                      >
                        <EditIcon className="size-4" /> ແກ້ໄຂ
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 text-destructive hover:text-destructive"
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ລຶບບໍລິການ?",
                            description: `ລຶບ "${r.name}"? ຖ້າຖືກໃຊ້ໃນວຽກ/ຂາຍແລ້ວ ຈະລຶບບໍ່ໄດ້.`,
                            actionText: "ລຶບ",
                            ActionProps: { variant: "destructive" },
                          });
                          if (ok) deleteMut.mutate(r.id);
                        }}
                      >
                        <Trash2Icon className="size-4" /> ລຶບ
                      </Button>
                    ) : null}
                  </div>
                );
              },
            } as TanstackReactTable.ColumnDef<ShopServiceRow>,
          ]
        : []),
    ],
    [canUpdate, canDelete, updateMut, deleteMut],
  );

  const rows = listQ.data?.data ?? [];
  const total = listQ.data?.meta.total ?? 0;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.catalog.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.services(shopId)} />
      </Header>
      <Main className="pb-8">
        <div className="flex flex-col gap-4 py-2 md:py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-700">
                <WrenchIcon className="size-5" />
              </span>
              <div>
                <h1 className="font-bold text-xl tracking-tight md:text-2xl">
                  ບໍລິການ
                </h1>
                <p className="text-muted-foreground text-xs md:text-sm">
                  ຈັດການ service / ຄ່າແຮງ ຂອງຮ້ານ (ໃຊ້ເປັນ preset ໃນວຽກສ້ອມ)
                </p>
              </div>
            </div>
            {canCreate ? (
              <Button type="button" onClick={openCreate}>
                <PlusIcon className="size-4" /> ເພີ່ມບໍລິການ
              </Button>
            ) : null}
          </div>

          <div className="relative max-w-md">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              placeholder="ຄົ້ນຊື່ບໍລິການ…"
              className="h-10 rounded-xl pl-9"
            />
          </div>

          {/* ຈັດຫົວຄໍລໍາສຸດທ້າຍ (ຈັດການ) ໄວ້ກາງ ໃຫ້ຕົງກັບປຸ່ມ */}
          <div className="[&_thead_th:last-child>div]:justify-center">
            <DataTable<ShopServiceRow, unknown>
              noDataMessage="ຍັງບໍ່ມີບໍລິການ — ກົດ 'ເພີ່ມບໍລິການ'"
              isLoading={listQ.isLoading}
              columns={columns}
              data={rows}
              offset={offset}
              limit={limit}
              totalCount={total}
              onPaginationChange={(p) => {
                setOffset(p.offset);
                setLimit(p.limit);
              }}
            />
          </div>
        </div>
      </Main>

      <Modal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={editing ? "ແກ້ໄຂບໍລິການ" : "ເພີ່ມບໍລິການ"}
        description="ໃສ່ຊື່ບໍລິການ ແລະ ລາຄາ"
        size="sm"
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="svc-name">ຊື່ບໍລິການ</Label>
            <Input
              id="svc-name"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="ເຊັ່ນ ປ່ຽນນ້ຳມັນເຄື່ອງ"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="svc-price">ລາຄາ</Label>
            <Input
              id="svc-price"
              inputMode="numeric"
              value={
                priceInput ? Number(priceInput).toLocaleString("en-US") : ""
              }
              onChange={(e) =>
                setPriceInput(e.target.value.replace(/[^0-9]/g, ""))
              }
              placeholder="0"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => setModalOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={createMut.isPending || updateMut.isPending}
            >
              {createMut.isPending || updateMut.isPending ? <Loader /> : null}
              {editing ? "ບັນທຶກ" : "ເພີ່ມ"}
            </Button>
          </div>
        </div>
      </Modal>
    </RequireMerchantPermission>
  );
}
